import React, { useState } from 'react';
import { 
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Trash2, 
  Edit, 
  Copy, 
  Pin,
  PinOff,
  Calendar,
  Palette,
  Tag,
  Eye,
  EyeOff,
  Share2,
  Folder
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type Note = {
  id: string;
  title: string;
  content: any;
  category: 'general' | 'pinned' | 'ideas' | 'reminders';
  color: string;
  visibility: 'private' | 'board_shared';
  is_pinned: boolean;
  reminder_date?: string;
  reminder_sent: boolean;
  position: number;
  tags: string[];
  created_at: string;
  updated_at: string;
  board_id?: string;
  user_id: string;
};

interface NoteContextMenuProps {
  note: Note;
  children: React.ReactNode;
  onEdit: () => void;
  onRefresh: () => void;
}

const colorOptions = [
  { name: 'Default', value: '#ffffff' },
  { name: 'Yellow', value: '#fef3c7' },
  { name: 'Green', value: '#d1fae5' },
  { name: 'Blue', value: '#dbeafe' },
  { name: 'Purple', value: '#ede9fe' },
  { name: 'Pink', value: '#fce7f3' },
  { name: 'Orange', value: '#fed7aa' },
];

const categoryOptions = [
  { value: 'general', label: 'General', icon: Folder },
  { value: 'pinned', label: 'Pinned', icon: Pin },
  { value: 'ideas', label: 'Ideas', icon: Tag },
  { value: 'reminders', label: 'Reminders', icon: Calendar },
] as const;

export const NoteContextMenu = ({ note, children, onEdit, onRefresh }: NoteContextMenuProps) => {
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .delete()
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Note deleted successfully",
      });
      onRefresh();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({
        title: "Error",
        description: "Failed to delete note",
        variant: "destructive"
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .insert({
          title: `${note.title} (Copy)`,
          content: note.content,
          category: note.category,
          color: note.color,
          visibility: note.visibility,
          tags: note.tags,
          board_id: note.board_id,
          user_id: note.user_id,
          position: 0
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Note duplicated successfully",
      });
      onRefresh();
    } catch (error) {
      console.error('Error duplicating note:', error);
      toast({
        title: "Error",
        description: "Failed to duplicate note",
        variant: "destructive"
      });
    }
  };

  const handleTogglePin = async () => {
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .update({ is_pinned: !note.is_pinned })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: note.is_pinned ? "Note unpinned" : "Note pinned",
      });
      onRefresh();
    } catch (error) {
      console.error('Error toggling pin:', error);
      toast({
        title: "Error",
        description: "Failed to update note",
        variant: "destructive"
      });
    }
  };

  const handleCategoryChange = async (newCategory: typeof note.category) => {
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .update({ category: newCategory })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Category updated successfully",
      });
      onRefresh();
    } catch (error) {
      console.error('Error updating category:', error);
      toast({
        title: "Error",
        description: "Failed to update category",
        variant: "destructive"
      });
    }
  };

  const handleColorChange = async (newColor: string) => {
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .update({ color: newColor })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Color updated successfully",
      });
      onRefresh();
    } catch (error) {
      console.error('Error updating color:', error);
      toast({
        title: "Error",
        description: "Failed to update color",
        variant: "destructive"
      });
    }
  };

  const handleVisibilityToggle = async () => {
    const newVisibility = note.visibility === 'private' ? 'board_shared' : 'private';
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .update({ visibility: newVisibility })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Note is now ${newVisibility === 'private' ? 'private' : 'shared with board'}`,
      });
      onRefresh();
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update visibility",
        variant: "destructive"
      });
    }
  };

  const handleSetReminderToday = async () => {
    const today = new Date();
    today.setHours(9, 0, 0, 0); // Set to 9 AM today
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .update({ 
          reminder_date: today.toISOString(),
          reminder_sent: false 
        })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Reminder set for today",
      });
      onRefresh();
    } catch (error) {
      console.error('Error setting reminder:', error);
      toast({
        title: "Error",
        description: "Failed to set reminder",
        variant: "destructive"
      });
    }
  };

  const handleSetReminderTomorrow = async () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0); // Set to 9 AM tomorrow
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .update({ 
          reminder_date: tomorrow.toISOString(),
          reminder_sent: false 
        })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Reminder set for tomorrow",
      });
      onRefresh();
    } catch (error) {
      console.error('Error setting reminder:', error);
      toast({
        title: "Error",
        description: "Failed to set reminder",
        variant: "destructive"
      });
    }
  };

  const handleClearReminder = async () => {
    try {
      const { error } = await supabase
        .from('reminder_notes')
        .update({ reminder_date: null, reminder_sent: false })
        .eq('id', note.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Reminder cleared",
      });
      onRefresh();
    } catch (error) {
      console.error('Error clearing reminder:', error);
      toast({
        title: "Error",
        description: "Failed to clear reminder",
        variant: "destructive"
      });
    }
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent className="w-56">
          <ContextMenuItem onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Note
          </ContextMenuItem>
          
          <ContextMenuItem onClick={handleDuplicate}>
            <Copy className="h-4 w-4 mr-2" />
            Duplicate
          </ContextMenuItem>

          <ContextMenuItem onClick={handleTogglePin}>
            {note.is_pinned ? (
              <>
                <PinOff className="h-4 w-4 mr-2" />
                Unpin Note
              </>
            ) : (
              <>
                <Pin className="h-4 w-4 mr-2" />
                Pin Note
              </>
            )}
          </ContextMenuItem>

          <ContextMenuSeparator />

          {/* Category */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <Folder className="h-4 w-4 mr-2" />
              Category
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {categoryOptions.map((category) => {
                const Icon = category.icon;
                return (
                  <ContextMenuItem
                    key={category.value}
                    onClick={() => handleCategoryChange(category.value)}
                    className={note.category === category.value ? "bg-accent" : ""}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {category.label}
                  </ContextMenuItem>
                );
              })}
            </ContextMenuSubContent>
          </ContextMenuSub>

          {/* Color */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <Palette className="h-4 w-4 mr-2" />
              Color
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              {colorOptions.map((color) => (
                <ContextMenuItem
                  key={color.value}
                  onClick={() => handleColorChange(color.value)}
                  className={note.color === color.value ? "bg-accent" : ""}
                >
                  <div
                    className="h-4 w-4 mr-2 rounded border border-border"
                    style={{ backgroundColor: color.value }}
                  />
                  {color.name}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>

          {/* Reminder */}
          <ContextMenuSub>
            <ContextMenuSubTrigger>
              <Calendar className="h-4 w-4 mr-2" />
              Reminder
            </ContextMenuSubTrigger>
            <ContextMenuSubContent>
              <ContextMenuItem onClick={handleSetReminderToday}>
                Set for Today
              </ContextMenuItem>
              <ContextMenuItem onClick={handleSetReminderTomorrow}>
                Set for Tomorrow
              </ContextMenuItem>
              {note.reminder_date && (
                <ContextMenuItem onClick={handleClearReminder}>
                  Clear Reminder
                </ContextMenuItem>
              )}
            </ContextMenuSubContent>
          </ContextMenuSub>

          {/* Visibility */}
          <ContextMenuItem onClick={handleVisibilityToggle}>
            {note.visibility === 'private' ? (
              <>
                <Share2 className="h-4 w-4 mr-2" />
                Share with Board
              </>
            ) : (
              <>
                <EyeOff className="h-4 w-4 mr-2" />
                Make Private
              </>
            )}
          </ContextMenuItem>

          <ContextMenuSeparator />

          <ContextMenuItem 
            onClick={() => setShowDeleteDialog(true)}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Note
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Note</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{note.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};